/**
 * The doctor a digitised prescription is attributed to (PHA-12). The picker listed the first 100
 * doctors by name with no search, and a doctor missing from the master couldn't be added, so such
 * an upload couldn't be digitised. It now searches (`q`: the name, or 3+ digits of the phone) and
 * can add a doctor as printed on the paper Rx; if one is already listed with that phone, they are
 * picked instead. Unverified doctors can be chosen and are labelled (D-17); the last 4 of the
 * registration number and phone tell same-named doctors apart.
 */

import { keepPreviousData, useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";

import { ApiError, describeError } from "../../api/errors";
import { addDoctor, listActiveDoctors, type DoctorOption, type NewDoctorInput } from "../../api/pharmacist";
import { useIdempotencyKey } from "../../api/useIdempotencyKey";
import { PhoneInput } from "../../components/ui/PhoneInput";
import { doctorName } from "../../lib/doctorName";
import { isValidIndianPhone } from "../../lib/phone";
import { useDebouncedValue } from "../../lib/useDebouncedValue";

export function DoctorPicker({ value, onChange }: { value: DoctorOption | null; onChange: (d: DoctorOption | null) => void }) {
  const [q, setQ] = useState("");
  const term = useDebouncedValue(q.trim());
  const [adding, setAdding] = useState(false);
  const { data: doctors = [], isFetching, isPlaceholderData, isError, refetch } = useQuery({
    queryKey: ["ph-doctors", term],
    // The signal cancels a search the next keystrokes made stale.
    queryFn: ({ signal }) => listActiveDoctors(term, signal),
    enabled: value === null,
    placeholderData: keepPreviousData,
  });

  const pick = (d: DoctorOption) => {
    onChange(d);
    setQ("");
    setAdding(false);
  };

  if (value) {
    return (
      <div className="flex items-start justify-between gap-2 rounded-lg border px-3 py-2 text-sm">
        <DoctorLabel doctor={value} />
        <button type="button" onClick={() => onChange(null)} className="shrink-0 text-xs text-brand hover:underline">Change</button>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <input
        type="search"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onKeyDown={(e) => {
          if (e.key !== "Enter") return;
          // Enter picks the top match instead of submitting the digitise form — only once the
          // list shows the results for what's typed.
          e.preventDefault();
          if (doctors.length > 0 && term === q.trim() && !isPlaceholderData) pick(doctors[0]);
        }}
        placeholder="Search by name or phone…"
        aria-label="Search doctors"
        className="w-full rounded-lg border px-3 py-1.5 text-sm"
      />
      {/* Fixed height: results arriving mustn't resize the (centred) modal under the cursor. */}
      <ul className="h-44 divide-y overflow-auto rounded-lg border text-sm" aria-label="Doctors" aria-busy={isFetching}>
        {doctors.map((d) => (
          <li key={d.id}>
            <button type="button" onClick={() => pick(d)} className="w-full px-3 py-2 text-left hover:bg-gray-50">
              <DoctorLabel doctor={d} />
            </button>
          </li>
        ))}
        {doctors.length === 0 && (
          <li className="px-3 py-2">
            {isFetching ? (
              <span className="text-gray-400">Loading doctors…</span>
            ) : isError ? (
              <span className="text-red-600">
                Couldn't load doctors.{" "}
                <button type="button" onClick={() => void refetch()} className="underline">Retry</button>
              </span>
            ) : (
              <span className="text-gray-500">{term ? `No doctor matches “${term}”.` : "No active doctors yet."}</span>
            )}
          </li>
        )}
      </ul>
      {adding ? (
        <AddDoctor initialName={/\d/.test(q) ? "" : q.trim()} onAdded={pick} onCancel={() => setAdding(false)} />
      ) : (
        <button type="button" onClick={() => setAdding(true)} className="text-sm text-brand hover:underline">
          + Doctor not in the list? Add them
        </button>
      )}
    </div>
  );
}

/** The name with a "Not verified" pill (D-17), then what tells same-named doctors apart. */
function DoctorLabel({ doctor: d }: { doctor: DoctorOption }) {
  const last4 = (masked: string | null | undefined) => (masked ? `••${masked.slice(-4)}` : null);
  const reg = last4(d.license_number_masked);
  const phone = last4(d.phone_masked);
  const meta = [d.specialisation, d.state_medical_council, reg && `Reg ${reg}`, phone && `Ph ${phone}`].filter(Boolean).join(" · ");
  return (
    <span className="block min-w-0">
      <span className="flex items-center gap-2">
        <span className="truncate font-medium">{doctorName(d.full_name)}</span>
        {d.verified === false && (
          <span className="shrink-0 rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-700">Not verified</span>
        )}
      </span>
      {meta && (
        <span className="block truncate text-xs text-gray-500" title={meta}>
          {meta}
        </span>
      )}
    </span>
  );
}

function AddDoctor({ initialName, onAdded, onCancel }: { initialName: string; onAdded: (d: DoctorOption) => void; onCancel: () => void }) {
  const [form, setForm] = useState<NewDoctorInput>({
    full_name: initialName, phone: "", state_medical_council: "", license_number: "", specialisation: "",
  });
  const [error, setError] = useState<string | null>(null);
  const set = (patch: Partial<NewDoctorInput>) => setForm((f) => ({ ...f, ...patch }));
  const body: NewDoctorInput = {
    full_name: form.full_name.trim(),
    phone: form.phone,
    state_medical_council: form.state_medical_council.trim(),
    license_number: form.license_number.trim(),
    specialisation: form.specialisation?.trim() || null,
  };
  const problem =
    body.full_name.length < 2 ? "Enter the doctor's name as printed."
      : !isValidIndianPhone(body.phone) ? "Enter the doctor's 10-digit mobile number."
        : body.state_medical_council.length < 2 ? "Enter the state medical council."
          : body.license_number.length < 2 ? "Enter the registration (licence) number."
            : null;
  const key = useIdempotencyKey();
  const add = useMutation({
    mutationFn: () => addDoctor(body, key.keyFor(body)),
    onMutate: () => setError(null),
    onSuccess: (d) => {
      key.reset();
      onAdded(d);
    },
    onError: async (e) => {
      // Already listed with this phone: pick them rather than add a second record, with their
      // listed details (e.g. "not verified").
      const details = e instanceof ApiError && e.code === "DOCTOR_ALREADY_EXISTS" ? e.details : null;
      if (details?.selectable && typeof details.doctor_id === "string") {
        const id = details.doctor_id;
        const listed = await listActiveDoctors(body.phone.replace(/\D/g, "").slice(-10)).catch(() => []);
        onAdded(listed.find((d) => d.id === id) ?? { id, full_name: String(details.full_name ?? body.full_name), specialisation: null });
        return;
      }
      setError(describeError(e));
    },
  });

  const field = (label: string, value: string, onChange: (v: string) => void, placeholder?: string) => (
    <label className="flex flex-col gap-1 text-xs text-gray-500">
      {label}
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && e.preventDefault()}
        placeholder={placeholder}
        className="rounded-lg border px-3 py-1.5 text-sm text-gray-900"
      />
    </label>
  );

  return (
    <div className="space-y-2 rounded-lg border border-dashed bg-gray-50 p-3">
      <p className="text-xs text-gray-500">
        Enter the doctor as printed on the prescription. They won't be contacted, and can't sign in until an admin
        turns their login on.
      </p>
      {field("Full name", form.full_name, (v) => set({ full_name: v }))}
      <PhoneInput label="Mobile number" value={form.phone} onChange={(v) => set({ phone: v })} />
      {field("State medical council", form.state_medical_council, (v) => set({ state_medical_council: v }), "e.g. Tamil Nadu Medical Council")}
      {field("Registration number", form.license_number, (v) => set({ license_number: v }), "As printed on the prescription")}
      {field("Specialisation (optional)", form.specialisation ?? "", (v) => set({ specialisation: v }))}
      {error ? <p className="text-sm text-red-600">{error}</p> : problem && <p className="text-xs text-gray-500">{problem}</p>}
      <div className="flex gap-2">
        <button type="button" onClick={onCancel} className="rounded-lg border px-3 py-1.5 text-sm">Cancel</button>
        <button
          type="button"
          onClick={() => add.mutate()}
          disabled={problem !== null || add.isPending}
          className="rounded-lg bg-brand px-3 py-1.5 text-sm font-medium text-brand-fg disabled:opacity-50"
        >
          {add.isPending ? "Adding…" : "Add doctor"}
        </button>
      </div>
    </div>
  );
}
